import { ChangeDetectionStrategy, ChangeDetectorRef, Component, HostListener, OnChanges, OnDestroy, inject, input } from "@angular/core";

@Component({
  selector: "app-ring-light",
  templateUrl: "./ring-light.component.html",
  styleUrls: ["./ring-light.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class RingLightComponent implements OnChanges, OnDestroy {
  private cdr = inject(ChangeDetectorRef);

  readonly intensity = input(70);
  readonly warmth = input(30);

  borderWidth = "";
  borderColor = "";
  boxShadow = "";
  mask = "";

  private currentWidth = 4;
  private readonly supportsHdr = false;

  ngOnChanges(): void {
    this.updateEffect();
  }

  @HostListener("document:mousemove", ["$event"])
  onMouseMove(event: MouseEvent): void {
    const mx = event.clientX;
    const my = event.clientY;
    const threshold = this.currentWidth + 60;

    const nearBorder =
      mx <= threshold ||
      mx >= window.innerWidth - threshold ||
      my <= threshold ||
      my >= window.innerHeight - threshold;

    if (!nearBorder) {
      if (this.mask) {
        this.mask = "";
        this.cdr.markForCheck();
      }
      return;
    }

    const cx = window.innerWidth / 2;
    const cy = window.innerHeight / 2;
    // Angle from center to mouse; 0 = top, clockwise
    const angle = Math.atan2(my - cy, mx - cx) * (180 / Math.PI) + 90;

    const gapDeg = 30;
    const fadeDeg = 15;
    const start = angle - gapDeg / 2 - fadeDeg;

    const newMask = `conic-gradient(from ${start}deg at 50% 50%, black 0deg, transparent ${fadeDeg}deg, transparent ${
      fadeDeg + gapDeg
    }deg, black ${fadeDeg * 2 + gapDeg}deg, black 360deg)`;

    if (this.mask !== newMask) {
      this.mask = newMask;
      this.cdr.markForCheck();
    }
  }

  @HostListener("document:mouseleave")
  onMouseLeave(): void {
    if (this.mask) {
      this.mask = "";
      this.cdr.markForCheck();
    }
  }

  ngOnDestroy(): void {
    document.documentElement.style.removeProperty("--av-ring-light-width");
  }

  private updateEffect(): void {
    const t = this.intensity() / 100;

    // Intensity controls 80% border width, 20% opacity
    const width = Math.round(4 + t * 0.8 * 56); // 4px to 48px
    const opacity = 0.5 + t * 0.2 * 2.5; // 0.5 to 1.0

    this.currentWidth = width;
    this.borderWidth = `${width}px`;
    document.documentElement.style.setProperty(
      "--av-ring-light-width",
      `${width}px`
    );

    if (this.supportsHdr) {
      // oklch: lightness can exceed 1.0 on HDR displays for true bright highlights
      // Warmth: 0 = cool blue-white (hue ~250), 100 = warm yellow-white (hue ~80)
      const hue = Math.round(250 - (this.warmth() / 100) * 170);
      const chroma = +(0.02 + (this.warmth() / 100) * 0.06).toFixed(3);
      const lightness = +(1.0 + t * 0.5).toFixed(3); // 1.0–1.5, HDR range

      this.borderColor = `oklch(${lightness} ${chroma} ${hue} / ${opacity})`;
      this.boxShadow = `inset 0 0 ${Math.round(
        20 + t * 30
      )}px 0 oklch(${lightness} ${chroma} ${hue} / ${opacity * 0.4})`;
    } else {
      // Fallback: sRGB rgba — cool white to warm yellow
      const g = Math.round(255 - (this.warmth() / 100) * 20);
      const b = Math.round(255 - (this.warmth() / 100) * 55);

      this.borderColor = `rgba(255, ${g}, ${b}, ${opacity})`;
      this.boxShadow = `inset 0 0 ${Math.round(
        20 + t * 30
      )}px 0 rgba(255, ${g}, ${b}, ${opacity * 0.4})`;
    }
  }
}
